import { type FC, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTagsQuery } from "../hooks/useTagsQuery";

interface TagOption {
  value: string;
  label: string;
}

interface TagsAutocompleteProps {
  label: string;
  value: string[];
  onChange: (tagIds: string[]) => void;
  disabled?: boolean;
  error?: boolean;
  /** Предел числа выбранных: дальше варианты недоступны (проверяет и сервер). */
  max?: number;
}

// Тот же паттерн, что ContentsAutocomplete/ProductsAutocomplete — список
// TagsList (useTagsQuery) вместо отдельного autocomplete-резолвера. Чипы
// уже выбранных тегов, не попавших в текущую страницу результатов, останутся
// без подписи, пока пользователь не введёт search, который их найдёт (см.
// ContentsAutocomplete.tsx).
export const TagsAutocomplete: FC<TagsAutocompleteProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  error = false,
  max,
}) => {
  const [search, setSearch] = useState("");

  const { data, loading } = useTagsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      filter: { search: search || undefined },
      page: 0,
      rowsPerPage: 20,
    },
  });

  const options: TagOption[] = (data?.tags?.nodes ?? []).map((node) => ({
    value: node.id,
    label: node.title,
  }));

  return (
    <Autocomplete<TagOption, true>
      multiple
      fullWidth
      disabled={disabled}
      filterOptions={(opts) => opts}
      loading={loading}
      options={options}
      getOptionLabel={(option) => option.label}
      getOptionDisabled={(option) =>
        max !== undefined &&
        value.length >= max &&
        !value.includes(option.value)
      }
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      value={options.filter((option) => value.includes(option.value))}
      onInputChange={(_event, newInputValue, reason) => {
        setSearch(reason === "reset" ? "" : newInputValue);
      }}
      onChange={(_event, newValue) => {
        onChange(newValue.map((option) => option.value));
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} error={error} />
      )}
    />
  );
};
